import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import useTbdCheck from "../../../functions/useTbdCheck";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectTelemetry, selectFieldDescriptions } from "../../../redux/selectors";
import TelemetryAccessories from "./TelemetryAccessories";
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice"; // Import the selector
import useFieldClass from "../../../functions/useFieldClass"; // Import the useFieldClass hook
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";

const Telemetry = (zone) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "telemetry"; // Name of the component

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const storedJobDetails = useSelector(selectTelemetry);

  const activetab = useSelector(
    (state) => state.activeElectricalTab?.selectedZone
  );

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const error = null;
  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  /****************Add dropdown options here*************************/

  const typeOptions =
    options?.filter((option) => option.DropdownType === "Telemetry Type")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const providerOptions =
    options?.filter((option) => option.DropdownType === "Telemetry Provider")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const softwareVersionOptions =
    options?.filter((option) => option.DropdownType === "Telemetry Software Version")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const rTUAccessoryOptions =
    options?.filter((option) => option.DropdownType === "RTU Accessory")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const stationToStationOptions =
    options?.filter((option) => option.DropdownType === "Station To Station")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

/**********************************************************************/


  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});

  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        type: jobDetails.Type || "",
        provider: jobDetails.Provider || "",
        softwareVersion: jobDetails.SoftwareVersion || "",
        rTUAccessory: jobDetails.RTUAccessory || "",
        latitude: jobDetails.Latitude || "",
        longitude: jobDetails.Longitude || "",
        endUserFirstName: jobDetails.EndUserFirstName || "",
        endUserLastName: jobDetails.EndUserLastName || "",
        endUserPhone: jobDetails.EndUserPhone || "",
        endUserEmail: jobDetails.EndUserEmail || "",
        comments: jobDetails.Comments || "",
        hasAccessories: jobDetails.HasAccessories || false,
        stationToStation: jobDetails.StationToStation || "",
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const { tbdFields } = useTbdCheck(formData, zone); // Pass zone to track TBD status for specific zone
  const zoneTbdFields = zone ? tbdFields?.[zone] : tbdFields;

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    );
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <div className="mechanical-responsive-grid telemetry-responsive">
          <Row className="mb-3">

            <Col>
              <Form.Group as={Row} controlId="type">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Type")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Type</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="type"
                    value={formData.type || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Type",
                        typeOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "Type",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.type} // Apply dynamic class
                    onChange={handleChange}
                  >
                    <option value="">Select Type</option>
                    {typeOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.type,
                      typeOptions
                    ) &&
                      formData.type && (
                        <option
                          value={formData.type}
                          style={{ color: "red" }}
                        >
                          *{formData.type}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="provider">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Provider")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Provider</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="provider"
                    value={formData.provider || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Provider",
                        providerOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "Provider",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.provider} // Apply dynamic class
                    onChange={handleChange}
                  >
                    <option value="">Select Provider</option>
                    {providerOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.provider,
                      providerOptions
                    ) &&
                      formData.provider && (
                        <option
                          value={formData.provider}
                          style={{ color: "red" }}
                        >
                          *{formData.provider}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">

            <Col>
              <Form.Group as={Row} controlId="softwareVersion">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Software Version")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Software Version</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="softwareVersion"
                    value={formData.softwareVersion || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Software Version",
                        softwareVersionOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "SoftwareVersion",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.softwareVersion} // Apply dynamic class
                    onChange={handleChange}
                  >
                    <option value="">Select Software Version</option>
                    {softwareVersionOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.softwareVersion,
                      softwareVersionOptions
                    ) &&
                      formData.softwareVersion && (
                        <option
                          value={formData.softwareVersion}
                          style={{ color: "red" }}
                        >
                          *{formData.softwareVersion}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="rTUAccessory">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("RTU Accessory")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>RTU Accessory</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="rTUAccessory"
                    value={formData.rTUAccessory || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "RTU Accessory",
                        rTUAccessoryOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "RTUAccessory",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.rTUAccessory} // Apply dynamic class
                    onChange={handleChange}
                  >
                    <option value="">Select RTU Accessory</option>
                    {rTUAccessoryOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.rTUAccessory,
                      rTUAccessoryOptions
                    ) &&
                      formData.rTUAccessory && (
                        <option
                          value={formData.rTUAccessory}
                          style={{ color: "red" }}
                        >
                          *{formData.rTUAccessory}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">

            <Col md={6}>
              <Form.Group as={Row} controlId="stationToStation">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Station To Station")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Station To Station</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="stationToStation"
                    value={formData.stationToStation || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Station To Station",
                        stationToStationOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "StationToStation",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.stationToStation} // Apply dynamic class
                    onChange={handleChange}
                  >
                    <option value="">Select Station To Station</option>
                    {stationToStationOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.stationToStation,
                      stationToStationOptions
                    ) &&
                      formData.stationToStation && (
                        <option
                          value={formData.stationToStation}
                          style={{ color: "red" }}
                        >
                          *{formData.stationToStation}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

          </Row>

          <Row className="mb-3 telemetry-coordinate-row">

            <Col>
              <Form.Group as={Row} controlId="latitude">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Latitude")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Latitude</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="latitude"
                    value={formData.latitude || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Latitude",
                        [],
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "Latitude",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.latitude} // Apply dynamic class
                    onChange={handleChange}
                    placeholder="Enter Latitude"
                  />
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="longitude">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Longitude")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Longitude</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="longitude"
                    value={formData.longitude || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Longitude",
                        [],
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "Longitude",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.longitude} // Apply dynamic class
                    onChange={handleChange}
                    placeholder="Enter Longitude"
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3 mechanical-form-full-width">

            <Col>
              <Form.Group controlId="comments">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Comments")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Comments</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="comments"
                  value={formData.comments || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Comments",
                      [],
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "Comments",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.comments} // Apply dynamic class
                  onChange={handleChange}
                  placeholder="Enter Comments"
                />
              </Form.Group>
            </Col>

          </Row>

          <h5 className="subsection-header mb-3">End User Details</h5>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="endUserFirstName">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("EndUserFirstName")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>First Name</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="endUserFirstName"
                    value={formData.endUserFirstName || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "End User First Name",
                        [],
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "EndUserFirstName",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.endUserFirstName}
                    onChange={handleChange}
                    placeholder="Enter First Name"
                  />
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="endUserLastName">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("EndUserLastName")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Last Name</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="endUserLastName"
                    value={formData.endUserLastName || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "End User Last Name",
                        [],
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "EndUserLastName",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.endUserLastName}
                    onChange={handleChange}
                    placeholder="Enter Last Name"
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="endUserPhone">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("EndUserPhone")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Phone Number</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="tel"
                    name="endUserPhone"
                    value={formData.endUserPhone || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "End User Phone",
                        [],
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "EndUserPhone",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.endUserPhone}
                    onChange={handleChange}
                    placeholder="Enter Phone Number"
                  />
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="endUserEmail">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("EndUserEmail")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Email</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="email"
                    name="endUserEmail"
                    value={formData.endUserEmail || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "End User Email",
                        [],
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "EndUserEmail",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.endUserEmail}
                    onChange={handleChange}
                    placeholder="Enter Email"
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <h4 className="subsection-header mb-3 d-flex align-items-center justify-content-center">
            <span className="me-2">Telemetry Accessories</span>
            <Form.Check
              type="switch"
              id="has-accessories-switch"
              checked={formData.hasAccessories} // Checked when value is true
              onFocus={handleFocus}
              onBlur={(e) => {
                handleBlur(
                  {
                    target: {
                      name: "hasAccessories",
                      value: formData.hasAccessories,
                    },
                  },
                  "Has Accessories",
                  [],
                  {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "HasAccessories",
                    tableKey,
                  }
                );
              }}
              disabled={isDisabled || (!roles.Admin && !roles.Apps)}
              className={`${zoneTbdFields?.hasAccessories ? "tbd-highlight" : ""}`}
              onChange={(e) => {
                handleChange({
                  target: {
                    name: "hasAccessories",
                    value: e.target.checked,
                  },
                });
              }}
            />
          </h4>
          {formData.hasAccessories && <TelemetryAccessories />}
          </div>

          <div></div>
        </div>
      )}
    </div>
  );
};

export default Telemetry;
